import authApi from '@api/authApi';
import locationApi from '@api/locationApi';
import orgApi from '@api/orgApi';
import { Lookup } from '@models/lookup';
import type { Scope } from '@models/scope';
import { useSearch } from '@tanstack/react-router';
import calcClaims from '@util/auth/calcClaims';
import type { ClaimQuery } from '@util/auth/hasClaim';
import resolveClaimQuery from '@util/auth/resolveClaimQuery';
import { useMemo } from 'react';

let cache:
  | {
      keys: any[];
      claims: Map<string, Scope[]>;
    }
  | undefined;

export default function useAuth() {
  const [me] = authApi.me.useCache();
  const roles = useMemo(() => Lookup.from(me?.roles ?? []), [me]);
  const [locations] = locationApi.lookup.useCache();
  const [orgs] = orgApi.lookup.useCache();
  const { locationId } = useSearch({ strict: false });

  function getCache() {
    const keys = [me, roles, locations, orgs, locationId];
    if (!cache || !cache.keys.every((key, i) => key === keys[i])) {
      cache = {
        keys,
        claims: new Map(),
      };
    }

    return cache;
  }

  function getUserClaims(scope: Scope | Scope[]) {
    if (!Array.isArray(scope)) {
      scope = [scope];
    }

    const claimsCache = getCache().claims;

    const key = JSON.stringify(scope);
    let userClaims = claimsCache.get(key);

    if (!userClaims) {
      userClaims = calcClaims({ user: me?.user ?? undefined, roles, locations, orgs }, scope);
      claimsCache.set(key, userClaims);
    }

    return userClaims;
  }

  function hasClaim(query: ClaimQuery, scope: Scope | Scope[]) {
    const userClaims = getUserClaims(scope);
    return resolveClaimQuery(query, userClaims);
  }

  return { ...me, loaded: !!me && !!locations && !!orgs, getUserClaims, hasClaim };
}
